import React, { useState, useEffect } from 'react'
import {
  ModalBackdrop,
  ModalContainer,
  ModalHeader,
  TabsNav,
  TabButton,
  ModalBody,
  ProfileViewCard,
  FormSection,
  FormGroup,
  CardsGrid,
  ThemeCard,
  MenuOptionCard,
  ColorPreviewBar,
  AlertMessage,
  ModalFooter,
} from './styles'
import {
  FiUser,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiSettings,
  FiCheck,
  FiSliders,
  FiEdit2,
  FiShield,
  FiX,
} from 'react-icons/fi'
import { MdPalette, MdClose } from 'react-icons/md'
import { useAuth } from '../../../context/AuthContext'
import { useCustomTheme } from '../../../context/Theme/ThemeContextData'
import api from '../../../services/axios'

interface ModalUserSettingsProps {
  isOpen: boolean
  onClose: () => void
}

type TabType = 'profile' | 'themes' | 'menu'

export default function ModalUserSettings({ isOpen, onClose }: ModalUserSettingsProps) {
  const { user, refreshUser } = useAuth()
  const {
    theme,
    setTheme,
    menuOrientation,
    setMenuOrientation,
    menuHighlight,
    setMenuHighlight,
  } = useCustomTheme()

  const [activeTab, setActiveTab] = useState<TabType>('profile')
  const [isEditing, setIsEditing] = useState(false)

  // Form states
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Feedback states
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (user) {
      setName(user.name || user.nickname || '')
      setEmail(user.email || '')
    }
    setIsEditing(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setSuccessMessage('')
    setErrorMessage('')
  }, [user, isOpen])

  if (!isOpen) return null

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    setSuccessMessage('')

    if (!name.trim()) {
      setErrorMessage('O nome não pode ficar vazio.')
      return
    }

    if (!email.trim()) {
      setErrorMessage('O e-mail não pode ficar vazio.')
      return
    }

    if (newPassword) {
      if (newPassword.length < 6) {
        setErrorMessage('A nova senha deve possuir no mínimo 6 caracteres.')
        return
      }
      if (newPassword !== confirmPassword) {
        setErrorMessage('A confirmação da senha não confere com a nova senha.')
        return
      }
      if (!currentPassword) {
        setErrorMessage('Informe a senha atual para confirmar a alteração.')
        return
      }
    }

    try {
      setLoading(true)
      const payload: {
        name: string
        email: string
        currentPassword?: string
        newPassword?: string
      } = {
        name: name.trim(),
        email: email.trim(),
      }

      if (newPassword) {
        payload.currentPassword = currentPassword
        payload.newPassword = newPassword
      }

      const response = await api.put('/auth/profile', payload)

      if (response.data) {
        setSuccessMessage('Perfil atualizado com sucesso!')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setIsEditing(false)
        await refreshUser()
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Não foi possível atualizar o perfil. Tente novamente.'
      setErrorMessage(msg)
    } finally {
      setLoading(false)
    }
  }

  const userInitials = (user?.nickname || user?.name || 'US').slice(0, 2).toUpperCase()

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Cabeçalho */}
        <ModalHeader>
          <div className='title-area'>
            <div className='icon-box'>
              <FiSettings />
            </div>
            <div>
              <h2>Configurações do Usuário</h2>
              <p>Gerencie seus dados pessoais, tema visual e navegação</p>
            </div>
          </div>
          <button className='close-btn' onClick={onClose} title='Fechar'>
            <MdClose />
          </button>
        </ModalHeader>

        {/* Abas */}
        <TabsNav>
          <TabButton
            $active={activeTab === 'profile'}
            onClick={() => {
              setActiveTab('profile')
              setErrorMessage('')
              setSuccessMessage('')
            }}
          >
            <FiUser size={16} />
            Perfil & Dados
          </TabButton>
          <TabButton
            $active={activeTab === 'themes'}
            onClick={() => {
              setActiveTab('themes')
              setErrorMessage('')
              setSuccessMessage('')
            }}
          >
            <MdPalette size={16} />
            Temas
          </TabButton>
          <TabButton
            $active={activeTab === 'menu'}
            onClick={() => {
              setActiveTab('menu')
              setErrorMessage('')
              setSuccessMessage('')
            }}
          >
            <FiSliders size={16} />
            Tema de Menu
          </TabButton>
        </TabsNav>

        {/* Conteúdo */}
        <ModalBody>
          {successMessage && (
            <AlertMessage $type='success'>
              <FiCheck size={18} />
              {successMessage}
            </AlertMessage>
          )}

          {errorMessage && (
            <AlertMessage $type='error'>
              <MdClose size={18} />
              {errorMessage}
            </AlertMessage>
          )}

          {/* ABA 1: PERFIL */}
          {activeTab === 'profile' && (
            <>
              {!isEditing ? (
                /* MODO VISUALIZAÇÃO */
                <ProfileViewCard>
                  <div className='header-card'>
                    <div className='user-summary'>
                      <div className='avatar'>{userInitials}</div>
                      <div className='names'>
                        <h3>{user?.name || user?.nickname || 'Usuário'}</h3>
                        <span className='badge-role'>
                          <FiShield style={{ marginRight: 4 }} />
                          {user?.role || 'Usuário'}
                        </span>
                      </div>
                    </div>
                    <button
                      className='edit-pencil-btn'
                      onClick={() => {
                        setIsEditing(true)
                        setErrorMessage('')
                        setSuccessMessage('')
                      }}
                      title='Alterar dados do perfil'
                    >
                      <FiEdit2 size={15} />
                      Editar Perfil
                    </button>
                  </div>

                  <div className='details-grid'>
                    <div className='detail-item'>
                      <span className='label'>Nome Completo</span>
                      <span className='val'>{user?.name || user?.nickname || '-'}</span>
                    </div>
                    <div className='detail-item'>
                      <span className='label'>E-mail</span>
                      <span className='val'>{user?.email || '-'}</span>
                    </div>
                    <div className='detail-item'>
                      <span className='label'>Login de Acesso</span>
                      <span className='val'>@{user?.nickname || '-'}</span>
                    </div>
                    <div className='detail-item'>
                      <span className='label'>Perfil do Sistema</span>
                      <span className='val' style={{ textTransform: 'capitalize' }}>
                        {user?.role || '-'}
                      </span>
                    </div>
                  </div>
                </ProfileViewCard>
              ) : (
                /* MODO EDIÇÃO */
                <form onSubmit={handleSaveProfile}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: 16, color: '#a6c10b', fontWeight: 700 }}>
                      Editar Informações
                    </h3>
                    <button
                      type='button'
                      onClick={() => setIsEditing(false)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                      }}
                    >
                      <FiX size={14} /> Cancelar edição
                    </button>
                  </div>

                  <FormSection>
                    <FormGroup>
                      <label>Nome Completo</label>
                      <div className='input-wrapper'>
                        <FiUser className='icon' />
                        <input
                          type='text'
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder='Seu nome completo'
                          required
                        />
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <label>E-mail de Acesso</label>
                      <div className='input-wrapper'>
                        <FiMail className='icon' />
                        <input
                          type='email'
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder='seuemail@empresa.com.br'
                          required
                        />
                      </div>
                    </FormGroup>

                    <div
                      style={{
                        height: 1,
                        background: 'rgba(255, 255, 255, 0.1)',
                        margin: '6px 0',
                      }}
                    />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#ffffff' }}>
                        Alterar Senha
                      </h4>
                      <p style={{ margin: 0, fontSize: 11, opacity: 0.7 }}>
                        Deixe em branco se não desejar alterar sua senha.
                      </p>
                    </div>

                    <FormGroup>
                      <label>Senha Atual</label>
                      <div className='input-wrapper'>
                        <FiLock className='icon' />
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder='Informe sua senha atual para validar'
                        />
                        <button
                          type='button'
                          className='toggle-password'
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                        </button>
                      </div>
                    </FormGroup>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <FormGroup>
                        <label>Nova Senha</label>
                        <div className='input-wrapper'>
                          <FiLock className='icon' />
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder='Mínimo 6 caracteres'
                          />
                          <button
                            type='button'
                            className='toggle-password'
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                          </button>
                        </div>
                      </FormGroup>

                      <FormGroup>
                        <label>Confirmar Nova Senha</label>
                        <div className='input-wrapper'>
                          <FiLock className='icon' />
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder='Repita a nova senha'
                          />
                          <button
                            type='button'
                            className='toggle-password'
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                          </button>
                        </div>
                      </FormGroup>
                    </div>
                  </FormSection>
                </form>
              )}
            </>
          )}

          {/* ABA 2: TEMAS */}
          {activeTab === 'themes' && (
            <div>
              <p style={{ margin: '0 0 16px 0', fontSize: 13, opacity: 0.8 }}>
                Selecione o tema da aplicação. A mudança é aplicada imediatamente.
              </p>

              <CardsGrid>
                {/* 1. Tema Padrão (Claro - Branco Frio) */}
                <ThemeCard
                  $selected={theme === 'light'}
                  $themeType='light'
                  onClick={() => setTheme('light')}
                >
                  <div className='preview-box'>
                    <div className='p-header'>
                      <div className='dot' />
                      <div className='dot' />
                    </div>
                    <div className='p-body'>
                      <div className='card-sample' />
                      <div className='card-sample' />
                    </div>
                  </div>
                  <div className='card-info'>
                    <h4>Padrão (Tema Claro)</h4>
                    {theme === 'light' && <span className='badge-active'>Ativo</span>}
                  </div>
                  <p>
                    Cor branca fria suave no fundo do projeto, mantendo os cartões e a visibilidade
                    perfeita.
                  </p>
                </ThemeCard>

                {/* 2. Tema Azul Escuro Dark */}
                <ThemeCard
                  $selected={theme === 'dark'}
                  $themeType='dark'
                  onClick={() => setTheme('dark')}
                >
                  <div className='preview-box'>
                    <div className='p-header'>
                      <div className='dot' />
                      <div className='dot' />
                    </div>
                    <div className='p-body'>
                      <div className='card-sample' />
                      <div className='card-sample' />
                    </div>
                  </div>
                  <div className='card-info'>
                    <h4>Azul Escuro Dark</h4>
                    {theme === 'dark' && <span className='badge-active'>Ativo</span>}
                  </div>
                  <p>O tema escuro oficial do ecossistema GID para menor cansaço visual.</p>
                </ThemeCard>
              </CardsGrid>
            </div>
          )}

          {/* ABA 3: TEMA DE MENU */}
          {activeTab === 'menu' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Seção 1: Orientação */}
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: 14, fontWeight: 700 }}>
                  Orientação do Menu
                </h4>
                <p style={{ margin: '0 0 14px 0', fontSize: 12, opacity: 0.8 }}>
                  Escolha entre barra superior horizontal ou barra lateral vertical organizada:
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <MenuOptionCard
                    $selected={menuOrientation === 'horizontal'}
                    onClick={() => setMenuOrientation('horizontal')}
                  >
                    <div className='illustration' style={{ flexDirection: 'column', gap: 4 }}>
                      <div
                        style={{
                          height: 16,
                          width: '100%',
                          background: '#a6c10b',
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '0 4px',
                        }}
                      >
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: '#fff' }} />
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: '#fff' }} />
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: '#fff' }} />
                      </div>
                      <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 4 }} />
                    </div>
                    <div className='info'>
                      <h4>Horizontal (Superior)</h4>
                      {menuOrientation === 'horizontal' && (
                        <span className='badge-active'>Ativo</span>
                      )}
                    </div>
                    <p>Menu clássico no topo da tela.</p>
                  </MenuOptionCard>

                  <MenuOptionCard
                    $selected={menuOrientation === 'vertical'}
                    onClick={() => setMenuOrientation('vertical')}
                  >
                    <div className='illustration' style={{ flexDirection: 'row', gap: 4 }}>
                      <div
                        style={{
                          width: 28,
                          height: '100%',
                          background: '#a6c10b',
                          borderRadius: 4,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 0',
                        }}
                      >
                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff' }} />
                        <div style={{ width: 10, height: 4, borderRadius: 1, background: '#fff' }} />
                        <div style={{ width: 10, height: 4, borderRadius: 1, background: '#fff' }} />
                      </div>
                      <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 4 }} />
                    </div>
                    <div className='info'>
                      <h4>Vertical (Lateral)</h4>
                      {menuOrientation === 'vertical' && (
                        <span className='badge-active'>Ativo</span>
                      )}
                    </div>
                    <p>
                      Move o filtro e o usuário para dentro da barra lateral, liberando 100% da
                      tela.
                    </p>
                  </MenuOptionCard>
                </div>
              </div>

              {/* Seção 2: Cores Destaque */}
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: 14, fontWeight: 700 }}>
                  Cores de Destaque do Menu
                </h4>
                <p style={{ margin: '0 0 14px 0', fontSize: 12, opacity: 0.8 }}>
                  Defina a cor dos ícones e abas ativas:
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <MenuOptionCard
                    $selected={menuHighlight === 'green'}
                    onClick={() => setMenuHighlight('green')}
                  >
                    <div
                      className='illustration'
                      style={{ alignItems: 'center', justifyContent: 'center' }}
                    >
                      <ColorPreviewBar>
                        <div className='chip' style={{ background: '#a6c10b' }} />
                        <div className='chip' style={{ background: '#8fa80a' }} />
                        <div className='chip' style={{ background: '#c4e015' }} />
                      </ColorPreviewBar>
                    </div>
                    <div className='info'>
                      <h4>Verde Padrão</h4>
                      {menuHighlight === 'green' && <span className='badge-active'>Ativo</span>}
                    </div>
                    <p>Verde corporativo tradicional do GID.</p>
                  </MenuOptionCard>

                  <MenuOptionCard
                    $selected={menuHighlight === 'mixed'}
                    onClick={() => setMenuHighlight('mixed')}
                  >
                    <div
                      className='illustration'
                      style={{ alignItems: 'center', justifyContent: 'center' }}
                    >
                      <ColorPreviewBar>
                        <div className='chip' style={{ background: '#38bdf8' }} />
                        <div className='chip' style={{ background: '#a855f7' }} />
                        <div className='chip' style={{ background: '#f97316' }} />
                        <div className='chip' style={{ background: '#10b981' }} />
                        <div className='chip' style={{ background: '#ec4899' }} />
                        <div className='chip' style={{ background: '#eab308' }} />
                      </ColorPreviewBar>
                    </div>
                    <div className='info'>
                      <h4>Cores Mistas</h4>
                      {menuHighlight === 'mixed' && <span className='badge-active'>Ativo</span>}
                    </div>
                    <p>Destaques com cores vibrantes exclusivas por ferramenta.</p>
                  </MenuOptionCard>
                </div>
              </div>
            </div>
          )}
        </ModalBody>

        {/* Rodapé com Ações */}
        <ModalFooter>
          {activeTab === 'profile' && isEditing ? (
            <>
              <button
                type='button'
                className='cancel-btn'
                onClick={() => setIsEditing(false)}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type='button'
                className='save-btn'
                onClick={handleSaveProfile}
                disabled={loading}
              >
                <FiCheck />
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </>
          ) : (
            <button type='button' className='cancel-btn' onClick={onClose}>
              Fechar
            </button>
          )}
        </ModalFooter>
      </ModalContainer>
    </ModalBackdrop>
  )
}
